import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { Badge, CustomButton, Text } from '../../../../../shared/uiComponents';
import { EyeHidden, TargetIcon, Trash } from '../../../../../shared/Assets/sessionDetailsSVGs';
import { BACKGROUND, RED, STROKE, TEXT } from '../../../../../shared/Helpers/colors';
import { ChevronDown, ChevronUp } from '../../../../../shared/Assets/svgIcons';
import { DTTTargetActions, TATargetActions } from './dataActions';
import TargetActions from './targetActions';

import { TargetSessionStatuses } from '../../../../../redux/API/ClientAPIHelpers/sessionsProperties';
import { DispatchProperties } from '../../../../../redux/store';
import { TargetTypes } from '../../../../../redux/API/ClientAPIHelpers/programTargetsProperties';
import {
	CustomTargetProperties,
	setHiddenTargetActive,
	setOpenTargetNew,
} from '../../../../../redux/State/clientSlice/sessionSlice';
import { useScreenWidth } from '../../../../../shared/Helpers/hooks';

const Target = ({ item, fetchData }: { fetchData: () => void; item: CustomTargetProperties }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const screenWidth = useScreenWidth();
	const isMobile = screenWidth <= 768;

	const [open, setOpen] = useState<boolean>(false);

	const toggleHandler = () => setOpen(!open);
	const closeHandler = () => setOpen(false);

	const {
		id,
		programId,
		programName,
		name,
		trials,
		type,
		independent,
		prompt,
		correctPercentage,
		targetInstructions,
		sd,
		steps,
		targetStatus,
	} = item;

	const isTargetHidden = targetStatus === TargetSessionStatuses.hidden;

	const setTargetActiveHandler = () => {
		dispatch(setOpenTargetNew(id));
		closeHandler();
	};

	const unhideHandler = () => {
		dispatch(setHiddenTargetActive(id));
	};

	const buttonStyle = isTargetHidden
		? {
				borderRadius: '999px',
				border: `1px solid ${STROKE.SECONDARY}`,
				background: BACKGROUND.PRIMARY[100],
				boxShadow: `0 1px 2px 0 rgba(0, 0, 0, 0.04)`,
			}
		: {};

	return (
		<div className={isTargetHidden ? 'targetCardHidden' : 'targetCard'}>
			<div className="targetHeaderInfo">
				<div className="icon">
					<TargetIcon />
				</div>
				<div className="text">
					<Text title={name} fontWeight="semiBold" color={isTargetHidden ? TEXT.TERTIARY : TEXT.PRIMARY} />
					{!isTargetHidden && <Text title={programName} fontWeight="medium" color={TEXT.TERTIARY} />}
				</div>
				{isTargetHidden ? (
					<CustomButton iconOnly={<EyeHidden />} buttonType="tertiary" onClick={unhideHandler} style={buttonStyle} />
				) : (
					<CustomButton
						iconOnly={open ? <ChevronUp /> : <ChevronDown />}
						buttonType="quaternary"
						onClick={toggleHandler}
						style={buttonStyle}
					/>
				)}
			</div>
			{!isTargetHidden && (
				<div className="targetDetails">
					<div className="badges">
						<Badge type="gray" label={`Target type: ${TargetTypes[type]}`} sizeType="sm" />
						<Badge type="gray" label={`Trials: ${trials}`} sizeType="sm" />
						<Badge type="gray" label={`Independent: ${independent}, ${correctPercentage}%`} sizeType="sm" />
						<Badge type="gray" label={`Prompted: ${prompt}`} sizeType="sm" />
					</div>
					{open && !isMobile && targetStatus === TargetSessionStatuses.active && (
						<TargetActions id={id} name={name} type={type} closeHandler={closeHandler} fetchData={fetchData} />
					)}
				</div>
			)}

			{open && (
				<div className="targetActionContent">
					<div>
						<Text title="Instruction:" fontWeight="semiBold" color={TEXT.SECONDARY} />
						<Text
							title={targetInstructions ? targetInstructions : 'No instructions'}
							type="psm"
							color={TEXT.TERTIARY}
						/>
					</div>
					<div>
						<Text title="SD:" fontWeight="semiBold" color={TEXT.SECONDARY} />
						<Text title={sd ? sd : 'No SD'} type="psm" color={TEXT.TERTIARY} />
					</div>
					<div className="targetActionsWrapper">
						{type === TargetTypes.DTT && <DTTTargetActions targetId={id} />}
						{type === TargetTypes.TA && <TATargetActions targetId={id} programId={programId} steps={steps} />}
					</div>
					{targetStatus === TargetSessionStatuses.open && (
						<div className="deleteAction">
							<CustomButton
								title="Remove"
								buttonType="secondary"
								startIcon={<Trash color={RED.BASE} />}
								onClick={setTargetActiveHandler}
								style={{
									color: RED.BASE,
									borderColor: RED.BASE,
									backgroundColor: RED.QUATERNARY,
								}}
								sizeType="md"
							/>
						</div>
					)}
					{isMobile && targetStatus === TargetSessionStatuses.active && (
						<TargetActions id={id} name={name} type={type} closeHandler={closeHandler} fetchData={fetchData} />
					)}
				</div>
			)}
		</div>
	);
};

export default Target;
